import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { Popper } from 'react-popper';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import defaultsDeep from 'lodash/defaultsDeep';
import forEach from 'lodash/forEach';
import { SearchSelectRow, OptionShape } from 'xm-spark';

import styles from './Menu.module.scss';

const setElementRefs = refs => element => {
  forEach(refs, ref => {
    if (typeof ref === 'function') {
      ref(element);
    } else {
      // eslint-disable-next-line
      ref.current = element;
    }
  });
};

const useWindowBinding = (type, fn, deps = []) => {
  useEffect(() => {
    document.addEventListener(type, fn);
    return () => {
      document.removeEventListener(type, fn);
    };
  }, deps);
};

const getCycledIndex = (index, length) => {
  if (index == null) {
    return index;
  }
  return (index + length) % length;
};

const invokeRef = ref => () => {
  if (ref.current) {
    ref.current();
  }
};

const classesShape = PropTypes.shape({
  disabled: PropTypes.string,
  focused: PropTypes.string,
  menuInner: PropTypes.string,
  menuOuter: PropTypes.string,
  optionValue: PropTypes.string,
  row: PropTypes.string,
  searchSelectInputWrapper: PropTypes.string,
  selectToggle: PropTypes.string,
  singleSelect: PropTypes.string,
  valueOptionWrapper: PropTypes.string,
});

// IE11 uses different names for some keys:
const IEKeyMap = {
  Down: 'ArrowDown',
  Up: 'ArrowUp',
  Esc: 'Escape',
};

// HoF pulled out of component for testing purposes:
export const getKeydownHandler =
  ({
    isOpen,
    keyboardFocusIndexRef,
    options,
    setFocusIndex,
    close,
    onChange,
  }) =>
  event => {
    if (isOpen) {
      const { current } = keyboardFocusIndexRef;
      const isNull = current == null;
      const key = IEKeyMap[event.key] || event.key;

      if (key === 'ArrowDown') {
        setFocusIndex(isNull ? 0 : current + 1);
      } else if (key === 'ArrowUp') {
        setFocusIndex(isNull ? -1 : current - 1);
      } else if (key === 'Escape') {
        close();
      } else if (key === 'Enter' && !isNull) {
        if (!options.length) {
          // No options implies adding a new option (or somehow triggering on no-results)
          // Either way, there's no option to change into so onChange will just throw errors.
          return;
        }
        onChange(options[current]);
      }
    }
  };

// HoF pulled out of component for testing purposes:
export const getFocusIndexSetter =
  ({ keyboardFocusIndexRef, listRef, options, setKeyboardFocusIndexState }) =>
  (index, doScroll = true) => {
    const rowIndex = getCycledIndex(index, options.length);
    setKeyboardFocusIndexState(rowIndex);
    // eslint-disable-next-line
    keyboardFocusIndexRef.current = rowIndex;
    if (doScroll && listRef.current && rowIndex != null) {
      listRef.current.scrollToItem(rowIndex);
    }
  };

const DEFAULT_POPPER_PROPS = {
  placement: 'bottom-start',
  modifiers: {
    preventOverflow: {
      escapeWithReference: true,
    },
  },
};

export default function Menu({
  classes: classesProp,
  close,
  isOpen,
  itemHeight,
  menuMaxHeight,
  menuMinHeight,
  menuRef,
  wrapperRef,
  noResultsNode,
  addOptionNode,
  onBlur,
  onChange,
  options,
  popperProps: popperPropsProp,
  portalNode,
  selectedValue,
  showCheckmarks,
  menuWidthMatchesInputWidth,
}) {
  const listRef = useRef();
  const scheduleUpdateRef = useRef();

  const classes = classesProp || {};
  const optionsLength = (options && options.length) || 0;

  const menuHeight = Math.max(
    menuMinHeight,
    Math.min(menuMaxHeight, optionsLength * itemHeight),
  );
  useEffect(invokeRef(scheduleUpdateRef), [menuHeight]);

  const [keyboardFocusIndex, setKeyboardFocusIndexState] = useState(null);
  // keyboardFocusIndexRef is used to prevent constantly calling addEventListener/removeEventListener on each keydown registered:
  const keyboardFocusIndexRef = useRef(null);
  const setFocusIndex = getFocusIndexSetter({
    keyboardFocusIndexRef,
    listRef,
    options,
    setKeyboardFocusIndexState,
  });

  useWindowBinding(
    'keydown',
    getKeydownHandler({
      isOpen,
      keyboardFocusIndexRef,
      options,
      setFocusIndex,
      close,
      onChange,
    }),
    [isOpen, keyboardFocusIndexRef, optionsLength],
  );

  const popperProps = useMemo(
    () => defaultsDeep(popperPropsProp, DEFAULT_POPPER_PROPS),
    [popperPropsProp],
  );

  if (!isOpen) {
    if (keyboardFocusIndex != null) {
      setFocusIndex(null);
    }
    return null;
  }

  const popper = (
    <Popper {...popperProps}>
      {({
        ref,
        style: popperStyle,
        placement,
        scheduleUpdate,
        outOfBoundaries,
      }) => {
        // eslint-disable-next-line
        scheduleUpdateRef.current = scheduleUpdate;
        const adjustedWidth = menuWidthMatchesInputWidth
          ? { width: wrapperRef.current?.clientWidth }
          : {};

        return (
          <div
            className={classnames(styles.menuOuter, classes.menuOuter)}
            style={
              popperProps.overrideStyle !== undefined
                ? {
                    ...adjustedWidth,
                    ...popperStyle,
                    ...popperProps.overrideStyle,
                  }
                : { ...adjustedWidth, ...popperStyle }
            }
            ref={setElementRefs([menuRef, ref])}
            data-placement={placement}
            data-out-of-boundaries={outOfBoundaries}
            tabIndex="-1"
            data-testid="menu-searchSelect"
            onBlur={onBlur}
          >
            {addOptionNode}
            {optionsLength ? (
              <FixedSizeList
                itemData={options}
                height={menuHeight}
                itemCount={options.length}
                itemSize={itemHeight}
                ref={listRef}
              >
                {rowArgs => {
                  const { index, style: innerStyle, data } = rowArgs;
                  const option = data[index];
                  const isFocused = index === keyboardFocusIndex;

                  return (
                    <SearchSelectRow
                      classes={classes}
                      labelKey="label"
                      option={option}
                      isFocused={isFocused}
                      selectValue={onChange}
                      itemHeight={itemHeight}
                      style={innerStyle}
                      onFocus={() => setFocusIndex(index, false)}
                      checked={option.checked ?? selectedValue === option.value}
                      showCheckmarks={showCheckmarks}
                    />
                  );
                }}
              </FixedSizeList>
            ) : (
              <div
                className={classnames(styles.noResults, classes.noResults)}
                data-testid="noResults-searchSelect"
              >
                {noResultsNode}
              </div>
            )}
          </div>
        );
      }}
    </Popper>
  );

  return portalNode ? ReactDOM.createPortal(popper, portalNode) : popper;
}

Menu.propTypes = {
  /**
   * list of classes to override
   */
  classes: classesShape.isRequired,
  /**
   * function to invoke to close the menu (e.g. on ESC keypress)
   */
  close: PropTypes.func.isRequired,
  /**
   * Flag to determine whether the menu should be rendered
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * menu item height
   */
  itemHeight: PropTypes.number.isRequired,
  /**
   * menu max height
   */
  menuMaxHeight: PropTypes.number.isRequired,
  /**
   * menu min height
   */
  menuMinHeight: PropTypes.number.isRequired,
  /**
   * Ref to assign the menu element to
   */
  menuRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  /**
   * Ref of the first div component
   */
  wrapperRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  /**
   * no results component
   */
  noResultsNode: PropTypes.node,
  /**
   * function to invoke when menu is blurred
   */
  onBlur: PropTypes.func.isRequired,
  /**
   * function to invoke when an item becomes selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * list of options
   */
  options: PropTypes.arrayOf(OptionShape),
  /**
   * props to pass along to popper
   */
  popperProps: PropTypes.shape(),
  /**
   * parent of popper
   */
  portalNode: PropTypes.node,
  /**
   * selected option value
   */
  selectedValue: PropTypes.string,
  /**
   * show checkmarks for selected values in the menu
   */
  showCheckmarks: PropTypes.bool.isRequired,
  /**
   * add custom value to list component
   */
  addOptionNode: PropTypes.node,
  /**
   * adjust width of the menu with the width of input
   */
  menuWidthMatchesInputWidth: PropTypes.bool,
};

Menu.defaultProps = {
  noResultsNode: <div>No matching values</div>,
  options: null,
  popperProps: {},
  portalNode: null,
  selectedValue: null,
  addOptionNode: null,
  wrapperRef: null,
  menuWidthMatchesInputWidth: false,
};

Menu.displayName = 'Menu';
